import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { useSelector } from "react-redux";
import { getAutoPrint } from '../../../utils/printerSettings';
import { electronPrint, isElectron } from '../../../services/electronPrint.service';

// Define the formatNumber function consistent with other components
const formatNumber = (value) => {
  if (value === undefined || value === null || value === "-") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// Format with no decimals for specific cases (like cash denominations)
const formatNumberNoDecimals = (value) => {
  if (value === undefined || value === null || value === "-") return "-";
  const num = typeof value === "string" ? parseFloat(value) : value;
  return num.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

export default function ModalPOSCashTransactionPrint({
  isOpen,
  printType = "start",
  cashBreakdown,
  businessName,
  branchName,
  vatCalculator,
  taxNumber,
  posInfo,
  posName,
  posNo,
  userFullName,
  transactionNumber = 1,
  totalCashFloat = 0,
  totalCashRemoval = 0,
  onPrintComplete,
}) {
  const { branch, pos } = useSelector((state) => state.pos);

  useEffect(() => {
    if (isOpen) {
      const handleAfterPrint = () => {
        if (onPrintComplete) {
          onPrintComplete();
        }
      };

      window.addEventListener("afterprint", handleAfterPrint);

      const printTimer = setTimeout(async () => {
        if (isElectron()) {
          await electronPrint('cashTransaction', {
            printType, cashBreakdown, businessName, branchName,
            taxNumber, posNo, posName, userFullName, transactionNumber,
            totalCashFloat, totalCashRemoval,
          });
          if (onPrintComplete) onPrintComplete();
          return;
        }
        if (getAutoPrint()) {
          window.print();
        }
      }, 500);

      return () => {
        clearTimeout(printTimer);
        window.removeEventListener("afterprint", handleAfterPrint);
      };
    }
  }, [isOpen, onPrintComplete, cashBreakdown, printType, businessName, branchName, taxNumber, posNo, posName, userFullName, transactionNumber, totalCashFloat, totalCashRemoval]);

  const generateTableData = () => {
    if (!cashBreakdown) {
      return [
        { credit: 1000, unit: 0 },
        { credit: 500, unit: 0 },
        { credit: 100, unit: 0 },
        { credit: 50, unit: 0 },
        { credit: 20, unit: 0 },
        { credit: 10, unit: 0 },
        { credit: 5, unit: 0 },
        { credit: 1, unit: 0 },
      ];
    }

    const result = [
      { credit: 1000, unit: Number(cashBreakdown.cash_1000) || 0 },
      { credit: 500, unit: Number(cashBreakdown.cash_500) || 0 },
      { credit: 100, unit: Number(cashBreakdown.cash_100) || 0 },
      { credit: 50, unit: Number(cashBreakdown.cash_50) || 0 },
      { credit: 20, unit: Number(cashBreakdown.cash_20) || 0 },
      { credit: 10, unit: Number(cashBreakdown.cash_10) || 0 },
      { credit: 5, unit: Number(cashBreakdown.cash_5) || 0 },
      { credit: 1, unit: Number(cashBreakdown.cash_1) || 0 },
    ];

    return result;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const formatCurrentDateTime = () => {
    const currentTime = moment().format("HH:mm");
    return currentTime;
  };

  const sum = (array) => {
    return array.reduce((sum, item) => sum + item.credit * item.unit, 0);
  };

  const calculateFinalAmount = (prevAmount, currentAmount, isDeposit) => {
    if (isDeposit) {
      return prevAmount + currentAmount;
    } else {
      return prevAmount - currentAmount;
    }
  };

  const getPosOrder = () => {
    if (posInfo && posInfo.length > 0 && posInfo[0]?.order) {
      return posInfo[0].order;
    }
    return posNo || "";
  };

  const getPosSellingDate = () => {
    if (
      posInfo &&
      posInfo.length > 0 &&
      posInfo[0]?.branchPosSelling &&
      posInfo[0].branchPosSelling.length > 0 &&
      posInfo[0].branchPosSelling[0].created_at
    ) {
      return formatDate(posInfo[0].branchPosSelling[0].created_at);
    }
    return moment().format("DD/MM/YYYY");
  };

  if (!isOpen) {
    return null;
  }

  const tableData = generateTableData();
  const totalAmount = sum(tableData);

  let headerText, timeText, totalText, title;
  const isDeposit = printType === "deposit";
  const isWithdraw = printType === "withdraw";
  const isStart = printType === "start";

  if (isStart) {
    headerText = "เงินสดตั้งต้น";
    timeText = "เวลาเริ่มขาย";
    totalText = "ยอดตั้งต้น";
    title = "";
  } else if (isDeposit) {
    headerText = "เงินสดนำเข้า";
    timeText = "เวลานำเข้า";
    totalText = "นำเข้ารวมทั้งหมด";
    title = "นำเข้า";
  } else if (isWithdraw) {
    headerText = "เงินสดนำออก";
    timeText = "เวลานำออก";
    totalText = "นำออกรวมทั้งหมด";
    title = "นำออก";
  }


  const prevSum = isDeposit
    ? totalCashFloat
    : isWithdraw
    ? totalCashRemoval
    : 0;

  const finalAmount = isStart
    ? totalAmount
    : calculateFinalAmount(prevSum, totalAmount, isDeposit);

  const content = (
    <div className="section-to-print-receipt-page pb-10 print:block hidden">
      <div
        id="section-to-print"
        className="w-[29.49vw] text-[0.98vw] font-[300] bg-[#FFF] px-[1.70vw] py-[1.31vw] flex flex-col gap-[0.59vw]"
      >
        <div className="text-center">
          <div>{businessName || ""}</div>
          <div>{branchName || ""}</div>
          <div>
            {branch?.branch_info?.business?.tax_number
              ? `TAX ID : ${branch?.branch_info?.business?.tax_number || ""}`
              : ""}{" "}
            {branch?.branch_info?.business?.settingVatCalculation?.code === "INCLUDED" ? "(Vat Included)" : branch?.branch_info?.business?.settingVatCalculation?.code === "SEPARATE" ? "(Vat Excluded)" : ""}
          </div>
        </div>
        <div className="flex flex-col">
          <div className="flex justify-between">
            <div className="whitespace-nowrap">
              วันที่ {getPosSellingDate()}
            </div>
            <div className="text-right">POS# : {posName || ""}</div>
          </div>
          <div className="flex justify-between">
            <div className="whitespace-nowrap">POS NO : {getPosOrder()}</div>
            <div className="text-right">พนักงาน : {userFullName || ""}</div>
          </div>
            <div className="flex justify-between">
              <div className="">
                {pos && pos[0].tax_number
                  ? `RD#${pos[0].tax_number}`
                  : "RD#-"}
              </div>
            {!isStart && (
              <div className="">
                {title}ครั้งที่ : {transactionNumber}
              </div>
            )}
            </div>
          <div className="flex justify-between">
            <div className="">{timeText}</div>
            <div className="">{formatCurrentDateTime()}</div>
          </div>

          <div className="border-dashed border-t-[0.04vw] border-black h-0"></div>

          <table className="w-full">
            <thead>
              <tr>
                <th className="font-[300] w-[33.33%] pb-[2.32%] text-left">
                  {headerText}
                </th>
                <th className="font-[300] text-right pr-[8.94%] pb-[2.32%]">
                  จำนวน
                </th>
                <th className="font-[300] w-[33.33%] text-right pb-[2.32%]">
                  รวม
                </th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((v, i) => {
                return (
                  <tr key={`tr-${i}`}>
                    <td className="pl-[2.5%]">
                      {formatNumberNoDecimals(v.credit)}
                    </td>
                    <td className="text-right pr-[12%]">
                      {formatNumberNoDecimals(v.unit)}
                    </td>
                    <td className="text-right">
                      {formatNumber(v.credit * v.unit)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.66%]"></div>

          {isStart ? (
            <div className="flex justify-between gap-[0.98vw]">
              <div className="text-p-12 font-[600]">{totalText}:</div>
              <div className="text-right text-p-12 font-[600]">
                {formatNumber(totalAmount)}
              </div>
            </div>
          ) : (
            <>
              <div className="flex justify-between gap-[0.98vw]">
                <div className="text-p-12 font-[600]">{totalText}:</div>
                <div className="text-right text-p-12 font-[600]">
                  {formatNumber(totalAmount)}
                </div>
              </div>

              <div className="flex justify-between gap-[0.98vw]">
                <div className="text-p-12">ยอดเงินก่อนหน้า:</div>
                <div className="text-right text-p-12">
                  {formatNumber(prevSum)}
                </div>
              </div>

              <div className="flex justify-between gap-[0.98vw]">
                <div className="text-p-12 font-[600]">ยอดเงินคงเหลือ:</div>
                <div className="text-right text-p-12 font-[600]">
                  {formatNumber(finalAmount)}
                </div>
              </div>
            </>
          )}

          <div className="flex justify-between gap-[0.98vw]">
            <div className="whitespace-nowrap">โน๊ต :</div>
          </div>
          {isStart && (
            <div className="text-[1.17vw] text-p-12 font-[600] mt-[3.31%] opacity-0">
              note here
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
